import type { ModuleStatValue, PreEngineeredModule } from "@edcc/game-data";

export interface ModuleStatSource {
  mass?: number | null;
  power?: number | null;
  integrity?: number | null;
  cost?: number | null;
  stats?: Record<string, ModuleStatValue> | null;
  preEngineered?: PreEngineeredModule | null;
}

export interface ModuleFact {
  label: string;
  value: string;
}

interface StatMeta {
  label: string;
  unit?: string;
}

const hiddenStats = new Set(["engineering", "experimental"]);

const statMeta: Record<string, StatMeta> = {
  damage: { label: "Damage" },
  thermload: { label: "Thermal load" },
  distdraw: { label: "Distributor draw", unit: "MW" },
  eps: { label: "Energy per second" },
  ammo: { label: "Ammo" },
  clip: { label: "Clip size" },
  reload: { label: "Reload time", unit: "s" },
  fireint: { label: "Fire interval", unit: "s" },
  range: { label: "Range", unit: "m" },
  falloff: { label: "Damage falloff", unit: "m" },
  shotspeed: { label: "Projectile speed", unit: "m/s" },
  burst: { label: "Burst" },
  burstrof: { label: "Burst rate of fire" },
  roundspershot: { label: "Rounds per shot" },
  jitter: { label: "Jitter", unit: "deg" },
  angle: { label: "Firing arc", unit: "deg" },
  maxangle: { label: "Maximum firing arc", unit: "deg" },
  facinglimit: { label: "Facing limit", unit: "deg" },
  breachdmg: { label: "Breach damage" },
  breachmin: { label: "Minimum breach damage" },
  breachmax: { label: "Maximum breach damage" },
  piercing: { label: "Piercing" },
  optmass: { label: "Optimal mass", unit: "t" },
  minmass: { label: "Minimum mass", unit: "t" },
  maxmass: { label: "Maximum mass", unit: "t" },
  optmul: { label: "Optimal multiplier" },
  minmul: { label: "Minimum multiplier" },
  maxmul: { label: "Maximum multiplier" },
  fuel: { label: "Fuel", unit: "t" },
  maxfuel: { label: "Maximum fuel per jump", unit: "t" },
  fuelmul: { label: "Fuel multiplier" },
  fuelpower: { label: "Fuel power curve" },
  jumpboost: { label: "Jump boost", unit: "ly" },
  pgen: { label: "Power generation", unit: "MW" },
  eff: { label: "Power efficiency" },
  syscap: { label: "Systems capacity", unit: "MJ" },
  engcap: { label: "Engines capacity", unit: "MJ" },
  wepcap: { label: "Weapons capacity", unit: "MJ" },
  sysrate: { label: "Systems recharge", unit: "MW" },
  engrate: { label: "Engines recharge", unit: "MW" },
  weprate: { label: "Weapons recharge", unit: "MW" },
  regen: { label: "Regeneration" },
  brokenregen: { label: "Broken regeneration" },
  shieldreinforcement: { label: "Shield reinforcement" },
  hullreinforcement: { label: "Hull reinforcement" },
  protection: { label: "Protection" },
  causres: { label: "Caustic resistance" },
  explres: { label: "Explosive resistance" },
  kinres: { label: "Kinetic resistance" },
  thermres: { label: "Thermal resistance" },
  shieldaddition: { label: "Shield addition" },
  shieldboost: { label: "Shield boost" },
  rate: { label: "Rate" },
  repair: { label: "Repair rate" },
  duration: { label: "Duration", unit: "s" },
  boot: { label: "Boot time", unit: "s" },
  spinup: { label: "Spin-up time", unit: "s" },
  time: { label: "Time", unit: "s" },
  scantime: { label: "Scan time", unit: "s" },
  scanrange: { label: "Scan range", unit: "m" },
  proberadius: { label: "Probe radius", unit: "m" },
  cooldown: { label: "Cooldown", unit: "s" },
  chargetime: { label: "Charge time", unit: "s" },
  charge: { label: "Charge", unit: "s" },
  chargeup: { label: "Charge-up time", unit: "s" },
  hacktime: { label: "Hack time", unit: "s" },
  cargo: { label: "Cargo capacity", unit: "t" },
  passengers: { label: "Passengers" },
  bays: { label: "Bays" },
  bins: { label: "Bins" },
  rebuildsperbay: { label: "Rebuilds per bay" },
  fightercost: { label: "Fighter cost" },
  maximum: { label: "Maximum" },
  ranget: { label: "Range type" },
  hps: { label: "HPS" },
  drain: { label: "Drain" },
  activepower: { label: "Active power", unit: "MW" },
  passive: { label: "Passive" },
  ammocost: { label: "Ammo cost" },
  minmulacceleration: { label: "Minimum acceleration multiplier" },
  optmulacceleration: { label: "Optimal acceleration multiplier" },
  maxmulacceleration: { label: "Maximum acceleration multiplier" },
  minmulspeed: { label: "Minimum speed multiplier" },
  optmulspeed: { label: "Optimal speed multiplier" },
  maxmulspeed: { label: "Maximum speed multiplier" },
  minmulrotation: { label: "Minimum rotation multiplier" },
  optmulrotation: { label: "Optimal rotation multiplier" },
  maxmulrotation: { label: "Maximum rotation multiplier" },
};

const statOrder = [
  "damage",
  "thermload",
  "distdraw",
  "eps",
  "fireint",
  "reload",
  "clip",
  "ammo",
  "range",
  "falloff",
  "shotspeed",
  "pgen",
  "eff",
  "syscap",
  "engcap",
  "wepcap",
  "sysrate",
  "engrate",
  "weprate",
  "optmass",
  "minmass",
  "maxmass",
  "maxfuel",
  "fuelmul",
  "fuelpower",
  "jumpboost",
  "shieldreinforcement",
  "hullreinforcement",
  "protection",
  "causres",
  "explres",
  "kinres",
  "thermres",
  "regen",
  "brokenregen",
  "shieldaddition",
  "shieldboost",
  "cargo",
  "passengers",
  "rate",
  "repair",
  "duration",
  "boot",
  "spinup",
  "scantime",
  "scanrange",
  "proberadius",
  "cooldown",
  "chargetime",
];

function orderFor(key: string): number {
  const index = statOrder.indexOf(key);
  return index === -1 ? statOrder.length : index;
}

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

export function formatModuleNumber(value: number): string {
  if (!Number.isFinite(value)) return "Not published";
  if (Number.isInteger(value)) return value.toLocaleString("en-US");
  return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

export function formatModuleStatValue(key: string, value: ModuleStatValue): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const unit = statMeta[key]?.unit;
  return `${formatModuleNumber(value)}${unit ? ` ${unit}` : ""}`;
}

export function moduleFacts(module: ModuleStatSource): ModuleFact[] {
  const facts: ModuleFact[] = [];
  if (module.mass !== null && module.mass !== undefined) {
    facts.push({ label: "Mass", value: `${formatModuleNumber(module.mass)} t` });
  }
  if (module.power !== null && module.power !== undefined) {
    facts.push({ label: "Power draw", value: `${formatModuleNumber(module.power)} MW` });
  }
  if (module.integrity !== null && module.integrity !== undefined) {
    facts.push({ label: "Integrity", value: formatModuleNumber(module.integrity) });
  }
  if (module.cost !== null && module.cost !== undefined) {
    facts.push({ label: "Cost", value: `${formatModuleNumber(module.cost)} cr` });
  }
  return facts;
}

export function moduleStatFacts(module: ModuleStatSource): ModuleFact[] {
  const stats = module.stats;
  if (!stats) return [];
  return Object.entries(stats)
    .filter(([key]) => !hiddenStats.has(key))
    .sort(([left], [right]) => orderFor(left) - orderFor(right) || left.localeCompare(right))
    .map(([key, value]) => ({
      label: statMeta[key]?.label ?? humanizeKey(key),
      value: formatModuleStatValue(key, value),
    }));
}
